// === EXPORT / IMPORT DE SAUVEGARDE ===
// Pragmatique "cloud save" pour un jeu front-only : on sérialise toutes les
// clés `deadtide_*` en un blob JSON que le joueur peut télécharger et
// re-importer sur un autre appareil / navigateur.
//
// Format du blob :
//   {
//     "exportFormatVersion": 1,
//     "gameVersion": "0.39.0-beta",
//     "exportedAt": "2026-05-11T19:00:00.000Z",
//     "pseudo": "Capitaine",
//     "keys": { "deadtide_meta": { ... }, "deadtide_camp": { ... }, ... }
//   }
//
// Choix de design :
//  - On embarque TOUTES les clés `deadtide_*` (y compris reports, tutos,
//    présets) pour avoir un backup complet. On exclut juste l'historique
//    des bugs pour ne pas balader des PII.
//  - À l'import, on **remplace** toutes les clés `deadtide_*` existantes —
//    pas de merge, le joueur a confirmé qu'il acceptait l'écrasement.

import { GAME_VERSION } from './MetaManager';
import { PlayerProfile } from './PlayerProfile';

/** Préfixe commun à toutes les clés de sauvegarde du jeu */
export const SAVE_KEY_PREFIX = 'deadtide_';

/** Version du format d'export — incrémenter si breaking change */
export const EXPORT_FORMAT_VERSION = 1;

/**
 * Clés volontairement exclues de l'export :
 *  - bug reports : peuvent contenir des descriptions perso, donc PII potentielle
 *  - last sent : timestamp anti-spam, sans intérêt à transporter
 */
const EXPORT_EXCLUDE_KEYS = new Set<string>([
  'deadtide_bug_reports',
  'deadtide_bug_last_sent',
  'deadtide_feedback_reports',
  'deadtide_feedback_last_sent',
]);

export interface SaveExportData {
  exportFormatVersion: number;
  gameVersion: string;
  exportedAt: string;
  pseudo: string | null;
  keys: Record<string, unknown>;
}

export interface SaveImportResult {
  ok: boolean;
  /** Nombre de clés effectivement restaurées */
  restored: number;
  /** Version du jeu d'origine de la sauvegarde, si présente */
  fromGameVersion: string | null;
  /** Message d'erreur lisible (vide si ok) */
  error: string | null;
}

/**
 * Lit le localStorage et construit un objet exportable. Pure : aucun effet
 * de bord côté DOM, juste un snapshot des clés du jeu.
 */
export function buildSaveExport(): SaveExportData {
  const keys: Record<string, unknown> = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(SAVE_KEY_PREFIX)) continue;
      if (EXPORT_EXCLUDE_KEYS.has(key)) continue;
      const raw = localStorage.getItem(key);
      if (raw === null) continue;
      // On essaie de parser en JSON pour un rendu propre ; sinon string brute
      try {
        keys[key] = JSON.parse(raw);
      } catch {
        keys[key] = raw;
      }
    }
  } catch {
    // localStorage indispo — on renvoie un export vide plutôt que de planter
  }
  return {
    exportFormatVersion: EXPORT_FORMAT_VERSION,
    gameVersion: GAME_VERSION,
    exportedAt: new Date().toISOString(),
    pseudo: PlayerProfile.getPseudo(),
    keys,
  };
}

/** Sérialise un export en chaîne JSON formatée (lisible si on l'ouvre). */
export function serializeSaveExport(data: SaveExportData = buildSaveExport()): string {
  return JSON.stringify(data, null, 2);
}

/**
 * Nom de fichier proposé : `deadtide-save-<pseudo>-<date>.json`.
 * Le pseudo est slugifié (non-ASCII → '-'). Date au format YYYY-MM-DD.
 */
export function suggestExportFilename(data: SaveExportData = buildSaveExport()): string {
  const date = data.exportedAt.slice(0, 10); // YYYY-MM-DD
  const rawPseudo = (data.pseudo ?? 'capitaine').toLowerCase();
  const slug = rawPseudo.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '') || 'capitaine';
  return `deadtide-save-${slug}-${date}.json`;
}

/**
 * Valide qu'un objet inconnu est un export Deadtide. Retourne l'export
 * typé (cast contrôlé) ou null si invalide, avec un message d'erreur lisible.
 */
export function parseSaveExport(raw: unknown): { data: SaveExportData | null; error: string | null } {
  if (raw === null || typeof raw !== 'object') {
    return { data: null, error: 'Fichier invalide : JSON non-objet.' };
  }
  const obj = raw as Record<string, unknown>;
  if (typeof obj.exportFormatVersion !== 'number') {
    return { data: null, error: 'Fichier invalide : champ `exportFormatVersion` manquant.' };
  }
  if (obj.exportFormatVersion > EXPORT_FORMAT_VERSION) {
    return {
      data: null,
      error: `Format trop récent (v${obj.exportFormatVersion}). Mets à jour le jeu pour l'importer.`,
    };
  }
  if (typeof obj.keys !== 'object' || obj.keys === null) {
    return { data: null, error: 'Fichier invalide : pas de section `keys`.' };
  }
  const keys = obj.keys as Record<string, unknown>;
  // Vérifier qu'au moins une clé `deadtide_*` est présente, sinon c'est
  // probablement un fichier complètement étranger.
  const keyNames = Object.keys(keys);
  const hasDeadtideKey = keyNames.some(k => k.startsWith(SAVE_KEY_PREFIX));
  if (!hasDeadtideKey) {
    return { data: null, error: 'Fichier invalide : aucune clé Deadtide trouvée.' };
  }
  return {
    data: {
      exportFormatVersion: obj.exportFormatVersion,
      gameVersion: typeof obj.gameVersion === 'string' ? obj.gameVersion : 'inconnue',
      exportedAt: typeof obj.exportedAt === 'string' ? obj.exportedAt : '',
      pseudo: typeof obj.pseudo === 'string' ? obj.pseudo : null,
      keys,
    },
    error: null,
  };
}

/**
 * Applique un export validé : efface toutes les clés `deadtide_*`
 * existantes, puis écrit celles de l'export. Retourne le nombre de
 * clés restaurées.
 *
 * Cette opération est destructive (pas de merge). L'appelant doit avoir
 * obtenu la confirmation explicite de l'utilisateur avant de l'appeler.
 */
export function applySaveImport(data: SaveExportData): SaveImportResult {
  try {
    // 1) Lister toutes les clés deadtide_* actuelles
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(SAVE_KEY_PREFIX)) toRemove.push(key);
    }
    // 2) Les supprimer (note : on évite de toucher aux clés non-deadtide)
    for (const k of toRemove) localStorage.removeItem(k);
    // 3) Écrire les clés du payload
    let restored = 0;
    for (const [key, value] of Object.entries(data.keys)) {
      if (!key.startsWith(SAVE_KEY_PREFIX)) continue;
      // Sérialiser : les exports gardent du JSON parsé, on doit re-stringifier
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);
      localStorage.setItem(key, serialized);
      restored++;
    }
    return {
      ok: true,
      restored,
      fromGameVersion: data.gameVersion ?? null,
      error: null,
    };
  } catch (e) {
    return {
      ok: false,
      restored: 0,
      fromGameVersion: data.gameVersion ?? null,
      error: e instanceof Error ? e.message : 'Erreur inconnue pendant l\'import.',
    };
  }
}
